import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button, SimpleTable, StatusBadge, Input, useToast } from '@src/components/ui';
import AdminLayout from '../AdminLayout';
import styles from './ClinicDetails.module.scss';
import { getClinic, updateClinic, getClinicOrderStats } from '../../../api/ClinicApi';
import { getClinicPaymentMethods } from '../../../api/PaymentMethodApi';
import { fetchClinicContacts, createClinicContact, updateClinicContact, deleteClinicContact, } from '../../../api/ClinicContactAdminApi';
import { fetchClinicSalesRepChanges } from '../../../api/ClinicSalesRepChangeAdminApi';
import { formatPhoneForTable, formatAddress, formatDateForTable, getStatusBadgeColors, formatStatusLabel } from '../utils/formatters';
import EditIcon from '@src/components/icons/EditIcon';
import StarIcon from '@src/components/icons/StarIcon';
import ClinicDetailsForm from './ClinicDetailsForm';
import AssignSalesRepPanel from './AssignSalesRepPanel';
import { createSalesRepHistoryColumns, normalizeSalesRepChange } from './salesRepHistory';
import CreateOrderModal from '@src/components/CreateOrderModal';
import { useImpersonation } from '@src/contexts/ImpersonationContext';
import { useCart } from '@src/contexts/CartContext';
import { CLINIC_CONTACT_ROLE_OPTIONS, clinicContactRoleLabel, normalizeClinicContact, } from './clinicContactTypes';
/**
 * Seeds the Edit Clinic slide-out form from a Clinic record. Shared by the initial fetch and by
 * every entry point that (re-)opens the edit form, so the form is always seeded from current
 * data rather than whatever was loaded when the page first rendered.
 */
const buildEditFormData = (clinic) => {
    const address = typeof clinic.address === 'object' && clinic.address ? clinic.address : {};
    const billingAddressRaw = clinic.billingAddress || clinic.billing_address;
    const billingAddress = typeof billingAddressRaw === 'object' && billingAddressRaw ? billingAddressRaw : {};
    const hasShipping = billingAddress &&
        typeof billingAddress === 'object' &&
        Object.keys(billingAddress).length > 0 &&
        (billingAddress.street1 || billingAddress.street_1 || billingAddress.street2 || billingAddress.street_2 ||
            billingAddress.city || billingAddress.state || billingAddress.postalCode || billingAddress.postal_code);
    return {
        name: clinic.name || '',
        street_1: address.street1 || address.street_1 || '',
        street_2: address.street2 || address.street_2 || '',
        city: address.city || '',
        state: address.state || '',
        postal_code: address.postalCode || address.postal_code || '',
        phone: (typeof clinic.phone === 'object' && clinic.phone?.formattedNumber) || formatPhoneForTable(clinic) || '',
        alternate_phone: (typeof clinic.alternatePhone === 'object' && clinic.alternatePhone?.formattedNumber) || formatPhoneForTable({ ...clinic, phone: clinic.alternatePhone }) || '',
        email: clinic.email || '',
        fax: (typeof clinic.fax === 'object' && clinic.fax?.formattedNumber) || (clinic.fax ? formatPhoneForTable({ ...clinic, phone: clinic.fax }) : ''),
        is_corporation_owned: clinic.isCorporationOwned || clinic.is_corporation_owned || false,
        corporation_name: clinic.customCorporationName || clinic.custom_corporation_name || clinic.corporateGroup?.name || clinic.corporate_group?.name || '',
        buying_groups: {
            psi: clinic.buyingGroups?.includes('PSI') || clinic.buying_groups?.includes('PSI') || false,
            verticalVet: clinic.buyingGroups?.includes('Vertical Vet') || clinic.buying_groups?.includes('Vertical Vet') || false,
            vmg: clinic.buyingGroups?.includes('VMG') || clinic.buying_groups?.includes('VMG') || false,
            tvc: clinic.buyingGroups?.includes('TVC') || clinic.buying_groups?.includes('TVC') || false,
            other: clinic.buyingGroups?.some((g) => !['PSI', 'Vertical Vet', 'VMG', 'TVC'].includes(g)) || clinic.buying_groups?.some((g) => !['PSI', 'Vertical Vet', 'VMG', 'TVC'].includes(g)) || false,
            otherText: clinic.buyingGroups?.find((g) => !['PSI', 'Vertical Vet', 'VMG', 'TVC'].includes(g)) || clinic.buying_groups?.find((g) => !['PSI', 'Vertical Vet', 'VMG', 'TVC'].includes(g)) || ''
        },
        termsAccepted: false,
        hasShippingAddress: !!hasShipping,
        shipping_street_1: billingAddress.street1 || billingAddress.street_1 || '',
        shipping_street_2: billingAddress.street2 || billingAddress.street_2 || '',
        shipping_city: billingAddress.city || '',
        shipping_state: billingAddress.state || '',
        shipping_postal_code: billingAddress.postalCode || billingAddress.postal_code || ''
    };
};
const ClinicDetails = () => {
    const { clinicId } = useParams();
    const navigate = useNavigate();
    const { startClinicOrder } = useImpersonation();
    const { clearAll } = useCart();
    const { showToast } = useToast();
    const [clinic, setClinic] = useState(null);
    const [createOrderModalOpen, setCreateOrderModalOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [orders, setOrders] = useState([]);
    const [orderStats, setOrderStats] = useState({
        totalOrders: 0,
        totalRevenue: 0,
        lastOrderDate: null,
    });
    const [slideOutOpen, setSlideOutOpen] = useState(false);
    const [slideOutType, setSlideOutType] = useState(null);
    const [statusChangeValue, setStatusChangeValue] = useState('');
    const [rejectionReason, setRejectionReason] = useState('');
    const [statusChangeSaving, setStatusChangeSaving] = useState(false);
    const [paymentMethodsLoading, setPaymentMethodsLoading] = useState(false);
    const [paymentMethodsError, setPaymentMethodsError] = useState(null);
    const [clinicPaymentMethods, setClinicPaymentMethods] = useState([]);
    const [contactsLoading, setContactsLoading] = useState(false);
    const [contactsError, setContactsError] = useState(null);
    const [clinicContacts, setClinicContacts] = useState([]);
    const [salesRepHistory, setSalesRepHistory] = useState([]);
    const [salesRepHistoryLoading, setSalesRepHistoryLoading] = useState(false);
    const [salesRepHistoryError, setSalesRepHistoryError] = useState(null);
    const [editingContact, setEditingContact] = useState(null);
    const [contactFormData, setContactFormData] = useState({
        first_name: '',
        last_name: '',
        email: '',
        phone_number: '',
        role: '',
        is_primary: false,
    });
    const [contactSaving, setContactSaving] = useState(false);
    const [contactDeleting, setContactDeleting] = useState(false);
    const [editFormData, setEditFormData] = useState({
        name: '',
        street_1: '',
        street_2: '',
        city: '',
        state: '',
        postal_code: '',
        phone: '',
        alternate_phone: '',
        email: '',
        fax: '',
        is_corporation_owned: false,
        corporation_name: '',
        buying_groups: {
            psi: false,
            verticalVet: false,
            vmg: false,
            tvc: false,
            other: false,
            otherText: ''
        },
        termsAccepted: false,
        hasShippingAddress: false,
        shipping_street_1: '',
        shipping_street_2: '',
        shipping_city: '',
        shipping_state: '',
        shipping_postal_code: ''
    });
    const fetchClinicPaymentMethods = async (id) => {
        try {
            setPaymentMethodsLoading(true);
            setPaymentMethodsError(null);
            const response = await getClinicPaymentMethods(id);
            if (response.body && response.body.results) {
                setClinicPaymentMethods(response.body.results);
            }
            else {
                setClinicPaymentMethods([]);
            }
        }
        catch (err) {
            console.error('Failed to load clinic payment methods', err);
            setPaymentMethodsError('Failed to load payment methods');
            setClinicPaymentMethods([]);
        }
        finally {
            setPaymentMethodsLoading(false);
        }
    };
    const loadClinicContacts = async (id) => {
        try {
            setContactsLoading(true);
            setContactsError(null);
            const response = await fetchClinicContacts({ clinic: id });
            const body = response.body;
            const rows = Array.isArray(body) ? body : body?.results || [];
            setClinicContacts(rows.map(normalizeClinicContact));
        }
        catch (err) {
            console.error('Failed to load clinic contacts', err);
            setContactsError('Failed to load contacts');
            setClinicContacts([]);
        }
        finally {
            setContactsLoading(false);
        }
    };
    const loadSalesRepHistory = async (id) => {
        try {
            setSalesRepHistoryLoading(true);
            setSalesRepHistoryError(null);
            const response = await fetchClinicSalesRepChanges({ clinic: id });
            const body = response.body;
            const rows = Array.isArray(body) ? body : body?.results || [];
            setSalesRepHistory(rows.map(normalizeSalesRepChange));
        }
        catch (err) {
            console.error('Failed to load sales rep history', err);
            setSalesRepHistoryError('Failed to load sales rep history');
            setSalesRepHistory([]);
        }
        finally {
            setSalesRepHistoryLoading(false);
        }
    };
    useEffect(() => {
        const fetchClinic = async () => {
            if (!clinicId)
                return;
            try {
                setLoading(true);
                const response = await getClinic(clinicId);
                const data = response.body;
                setClinic(data);
                await fetchClinicPaymentMethods(clinicId);
                await loadClinicContacts(clinicId);
                await loadSalesRepHistory(clinicId);
                if (data) {
                    setEditFormData(buildEditFormData(data));
                }
                try {
                    const orderData = await getClinicOrderStats(clinicId);
                    setOrderStats({
                        totalOrders: orderData.totalOrders,
                        totalRevenue: orderData.totalRevenue,
                        lastOrderDate: orderData.lastOrderDate,
                    });
                    setOrders(orderData.recentOrders || []);
                }
                catch (err) {
                    setOrderStats({
                        totalOrders: 0,
                        totalRevenue: 0,
                        lastOrderDate: null,
                    });
                    setOrders([]);
                }
            }
            catch (err) {
                setError('Failed to load clinic details');
                console.error(err);
            }
            finally {
                setLoading(false);
            }
        };
        fetchClinic();
    }, [clinicId]);
    const [orderStatusFilter, setOrderStatusFilter] = useState('');
    const handleOrderFilterChange = (value) => {
        setOrderStatusFilter(value);
    };
    if (loading)
        return _jsx(AdminLayout, { children: _jsx("div", { children: "Loading..." }) });
    if (error)
        return _jsx(AdminLayout, { children: _jsx("div", { children: error }) });
    if (!clinic)
        return _jsx(AdminLayout, { children: _jsx("div", { children: "Clinic not found" }) });
    const openEditClinic = () => {
        setEditFormData(buildEditFormData(clinic));
        setSlideOutType('editInfo');
        setSlideOutOpen(true);
    };
    const openChangeStatus = () => {
        setStatusChangeValue(clinic.approval_status || clinic.approvalStatus || '');
        setRejectionReason(clinic.rejection_reason || '');
        setSlideOutType('changeStatus');
        setSlideOutOpen(true);
    };
    const openAssignSalesRep = () => {
        setSlideOutType('salesRep');
        setSlideOutOpen(true);
    };
    const openAddContact = () => {
        setEditingContact(null);
        setContactFormData({ first_name: '', last_name: '', email: '', phone_number: '', role: '', is_primary: false });
        setSlideOutType('contact');
        setSlideOutOpen(true);
    };
    const openEditContact = (contact) => {
        setEditingContact(contact);
        setContactFormData({
            first_name: contact.first_name,
            last_name: contact.last_name,
            email: contact.email || '',
            phone_number: contact.phone?.number || '',
            role: contact.role,
            is_primary: contact.is_primary,
        });
        setSlideOutType('contact');
        setSlideOutOpen(true);
    };
    const closeContactPanel = () => {
        setSlideOutOpen(false);
        setSlideOutType(null);
        setEditingContact(null);
    };
    const handleSaveContact = async () => {
        if (!clinicId)
            return;
        if (!contactFormData.first_name.trim()) {
            showToast({ message: 'First name is required', variant: 'error', noBorder: true });
            return;
        }
        setContactSaving(true);
        try {
            const payload = {
                first_name: contactFormData.first_name.trim(),
                last_name: contactFormData.last_name.trim(),
                email: contactFormData.email.trim(),
                phone_number: contactFormData.phone_number.trim(),
                role: contactFormData.role,
                is_primary: contactFormData.is_primary,
            };
            if (editingContact) {
                await updateClinicContact(editingContact.id, payload);
                showToast({ message: 'Contact updated', variant: 'success', noBorder: true });
            }
            else {
                await createClinicContact({ ...payload, clinic: clinicId });
                showToast({ message: 'Contact added', variant: 'success', noBorder: true });
            }
            await loadClinicContacts(clinicId);
            closeContactPanel();
        }
        catch (err) {
            console.error('Failed to save clinic contact', err);
            showToast({ message: 'Failed to save contact', variant: 'error', noBorder: true });
        }
        finally {
            setContactSaving(false);
        }
    };
    const handleDeleteContact = async () => {
        if (!clinicId || !editingContact)
            return;
        setContactDeleting(true);
        try {
            await deleteClinicContact(editingContact.id);
            showToast({ message: 'Contact removed', variant: 'success', noBorder: true });
            await loadClinicContacts(clinicId);
            closeContactPanel();
        }
        catch (err) {
            console.error('Failed to delete clinic contact', err);
            showToast({ message: 'Failed to remove contact', variant: 'error', noBorder: true });
        }
        finally {
            setContactDeleting(false);
        }
    };
    const handleCreateOrderSubmit = async (selectedAddress) => {
        // Clear the cart first so every clinic order starts empty - it can't carry over items left
        // behind from a previous clinic's order.
        await clearAll().catch(() => undefined);
        startClinicOrder(clinic, selectedAddress);
        setCreateOrderModalOpen(false);
        navigate('/products');
    };
    const status = clinic.approvalStatus || clinic.approval_status || 'pending';
    // Orders can only be started for approved clinics - pending, rejected and on-hold are blocked.
    const canCreateOrder = status === 'approved';
    const createdBy = clinic.createdBy || clinic.created_by;
    const modifiedBy = clinic.modifiedBy || clinic.modified_by;
    const createdAt = clinic.createdAt || clinic.created_at || clinic.date_registered;
    const modifiedAt = clinic.updatedAt || clinic.updated_at;
    const { background: statusBg, text: statusColor } = getStatusBadgeColors(status);
    const filteredOrders = orderStatusFilter
        ? orders.filter((order) => order.status === orderStatusFilter.toLowerCase())
        : orders;
    const orderColumns = [
        {
            key: 'orderNumber',
            header: 'Order Number',
            render: (order) => order.orderNumber,
        },
        {
            key: 'date',
            header: 'Date',
            render: (order) => formatDateForTable(order.date),
        },
        {
            key: 'total',
            header: 'Total',
            render: (order) => `$${order.total.toFixed(2)}`,
        },
        {
            key: 'status',
            header: 'Status',
            render: (order) => {
                const statusColors = {
                    delivered: { background: '#EBFEF6', text: '#00654F' },
                    submitted: { background: '#E8F4FD', text: '#0066CC' },
                    shipped: { background: '#FFF4E5', text: '#FF8C00' },
                    cancelled: { background: '#FFF0F3', text: '#AC0824' },
                };
                const colors = statusColors[order.status] || statusColors.submitted;
                return (_jsx(StatusBadge, { label: order.status.charAt(0).toUpperCase() + order.status.slice(1), bgColor: colors.background, color: colors.text }));
            },
        },
    ];
    const contactDisplayName = (contact) => `${contact.first_name} ${contact.last_name}`.trim() || 'N/A';
    // The clinic's current primary, if it's a different contact than the one being edited - used
    // to tell the admin that checking "Primary contact" will replace it.
    const currentPrimaryContact = clinicContacts.find((contact) => contact.is_primary && contact.id !== editingContact?.id);
    const contactColumns = [
        {
            key: 'name',
            header: 'Name',
            render: (contact) => (_jsxs("span", { children: [contactDisplayName(contact), contact.is_primary && (_jsx("span", { className: styles.primaryStar, children: _jsx(StarIcon, {}) }))] })),
        },
        {
            key: 'role',
            header: 'Clinic Role',
            render: (contact) => contact.role ? clinicContactRoleLabel(contact.role) : 'N/A',
        },
        {
            key: 'phone',
            header: 'Phone',
            render: (contact) => contact.phone?.number || 'N/A',
        },
        {
            key: 'email',
            header: 'Email',
            render: (contact) => contact.email || 'N/A',
        },
    ];
    return (_jsxs(AdminLayout, { children: [_jsxs("div", { className: styles.container, children: [_jsx("div", { className: styles.breadcrumbRow, children: _jsxs("div", { className: styles.breadcrumbs, children: [_jsx("span", { className: styles.backLink, onClick: () => navigate('/web-admin/clinics'), children: "Back to Clinics" }), _jsx("span", { className: styles.separator, children: "/" }), _jsx("span", { className: styles.current, children: clinic.name })] }) }), _jsxs("div", { className: styles.titleRow, children: [_jsx("h1", { children: clinic.name }), _jsx("span", { className: styles.pipe, children: "|" }), _jsxs("span", { className: styles.location, children: [(typeof clinic.address === 'object' && clinic.address?.city) || 'City', ", ", (typeof clinic.address === 'object' && clinic.address?.state) || 'State'] }), _jsx("span", { className: styles.pipe, children: "|" }), _jsx(StatusBadge, { label: formatStatusLabel(status), bgColor: statusBg, color: statusColor }), _jsxs("div", { className: styles.headerActions, children: [_jsx(Button, { variant: "secondaryV2", size: "small", label: "Edit Clinic Info", onClick: openEditClinic }), _jsx(Button, { variant: "secondaryV2", size: "small", label: "Change Status", onClick: openChangeStatus }), _jsx(Button, { variant: "primaryV2", size: "small", label: "Create Order", disabled: !canCreateOrder, title: canCreateOrder ? undefined : 'Orders can only be created for approved clinics.', onClick: () => setCreateOrderModalOpen(true) })] })] }), _jsx("div", { className: styles.divider }), _jsxs("div", { className: styles.mainContent, children: [_jsxs("div", { className: styles.leftSection, children: [_jsxs("div", { className: styles.statsRow, children: [_jsxs("div", { className: `${styles.statBox} ${styles.green}`, children: [_jsx("div", { className: styles.statNumber, children: orderStats.totalOrders }), _jsx("div", { className: styles.statLabel, children: "Total Orders" })] }), _jsxs("div", { className: `${styles.statBox} ${styles.blue}`, children: [_jsxs("div", { className: styles.statNumber, children: ["$", orderStats.totalRevenue.toLocaleString()] }), _jsx("div", { className: styles.statLabel, children: "Total Revenue from Clinic" })] }), _jsxs("div", { className: `${styles.statBox} ${styles.brown}`, children: [_jsx("div", { className: styles.statNumber, children: orderStats.lastOrderDate ? formatDateForTable(orderStats.lastOrderDate) : 'No Orders' }), _jsx("div", { className: styles.statLabel, children: "Last Order Date" })] })] }), _jsx("div", { className: styles.ordersSection, children: _jsx(SimpleTable, { ...{
                                                columns: orderColumns,
                                                data: filteredOrders,
                                                tableHeading: 'Recent Orders',
                                                rowHeight: '48px',
                                                hideTopBorder: true,
                                                enableSearch: true,
                                                centerSearch: true,
                                                enablePagination: true,
                                                paginationItemsLabel: 'Orders',
                                                paginationText: 'Orders per page',
                                            } }) }), _jsx("div", { className: styles.ordersSection, children: contactsLoading ? (_jsx("div", { children: "Loading contacts..." })) : contactsError ? (_jsx("div", { children: contactsError })) : (_jsx(SimpleTable, { ...{
                                                columns: contactColumns,
                                                data: clinicContacts,
                                                tableHeading: 'Contacts',
                                                rowHeight: '48px',
                                                hideTopBorder: true,
                                                onRowClick: (contact) => openEditContact(contact),
                                                customButton: (_jsx(Button, { variant: "primaryV2", size: "small", label: "+ Add Contact", onClick: openAddContact })),
                                            } })) }), _jsx("div", { className: styles.ordersSection, "data-testid": "sales-rep-history", children: salesRepHistoryLoading ? (_jsx("div", { children: "Loading sales rep history..." })) : salesRepHistoryError ? (_jsx("div", { children: salesRepHistoryError })) : salesRepHistory.length === 0 ? (_jsx("div", { children: "No sales rep changes recorded." })) : (_jsx(SimpleTable, { ...{
                                                columns: createSalesRepHistoryColumns(),
                                                data: salesRepHistory,
                                                tableHeading: 'Sales Rep History',
                                                rowHeight: '48px',
                                                hideTopBorder: true,
                                            } })) })] }), _jsx("div", { className: styles.rightSection, children: _jsxs("div", { className: styles.clinicInfoCard, children: [_jsx("h2", { className: styles.clinicInfoTitle, children: "Clinic Info" }), _jsx("div", { className: styles.titleBorder }), _jsxs("div", { className: styles.clinicIdSection, children: [_jsxs("div", { className: styles.clinicId, children: ["ID # ", clinic.accountNumber || clinic.displayId || clinic.id.substring(0, 8).toUpperCase()] }), _jsxs("div", { className: styles.clinicNameContainer, children: [_jsx("div", { className: styles.clinicName, children: clinic.name }), _jsx("button", { className: styles.editIconButton, onClick: openEditClinic, title: "Edit Clinic Info", children: _jsx(EditIcon, { fill: "#545B5B", width: 16, height: 16 }) })] })] }), _jsx("div", { className: styles.idSectionBorder }), _jsxs("div", { className: styles.infoSection, children: [_jsx("h3", { children: "Contact Details" }), _jsxs("div", { className: styles.addressSection, children: [_jsx("label", { children: "Shipping Address" }), _jsx("div", { className: styles.addressBlock, children: formatAddress(clinic.billingAddress || clinic.billing_address || clinic.address) })] }), _jsxs("div", { className: styles.addressSection, children: [_jsx("label", { children: "Business Address" }), _jsx("div", { className: styles.addressBlock, children: formatAddress(clinic.address) })] }), _jsxs("div", { className: styles.contactSection, children: [_jsxs("div", { className: styles.contactItem, children: [_jsx("label", { children: "Email" }), _jsx("a", { href: `mailto:${clinic.email}`, children: clinic.email || 'N/A' })] }), _jsxs("div", { className: styles.contactItem, children: [_jsx("label", { children: "Phone" }), _jsx("a", { href: `tel:${clinic.phone}`, children: formatPhoneForTable(clinic) || 'N/A' })] }), _jsxs("div", { className: styles.contactItem, children: [_jsx("label", { children: "Alternate Phone" }), _jsx("a", { href: `tel:${clinic.alternatePhone}`, children: formatPhoneForTable({ ...clinic, phone: clinic.alternatePhone }) || 'N/A' })] }), _jsxs("div", { className: styles.contactItem, children: [_jsx("label", { children: "Fax" }), clinic.fax ? (_jsx("a", { href: `tel:${clinic.fax}`, children: formatPhoneForTable({ ...clinic, phone: clinic.fax }) })) : (_jsx("span", { children: "N/A" }))] })] }), _jsx("div", { className: styles.contactSectionBorder })] }), _jsxs("div", { className: styles.infoSection, children: [_jsx("div", { className: styles.paymentMethodsTitle, children: "Payment Methods" }), _jsx("div", { className: styles.paymentCards, children: paymentMethodsLoading ? (_jsx("div", { className: styles.noPaymentMethod, children: "Loading payment methods\u2026" })) : paymentMethodsError ? (_jsx("div", { className: styles.noPaymentMethod, children: paymentMethodsError })) : clinicPaymentMethods.length > 0 ? (clinicPaymentMethods.map((card) => {
                                                        const lastFour = card.lastFourDigits ??
                                                            card.last_four_digits ??
                                                            card.last4 ??
                                                            '0000';
                                                        const expMonth = card.expirationMonth ?? card.expiration_month;
                                                        const expYear = card.expirationYear ?? card.expiration_year;
                                                        const formattedMonth = typeof expMonth === 'number'
                                                            ? expMonth.toString().padStart(2, '0')
                                                            : expMonth
                                                                ? String(expMonth).padStart(2, '0')
                                                                : '??';
                                                        const formattedYear = typeof expYear === 'number'
                                                            ? expYear.toString().slice(-2)
                                                            : expYear
                                                                ? String(expYear).slice(-2)
                                                                : '??';
                                                        return (_jsxs("div", { className: styles.cardItem, children: [_jsxs("span", { className: styles.cardNumber, children: ["\u2022\u2022\u2022\u2022 \u2022\u2022\u2022\u2022 \u2022\u2022\u2022\u2022 ", lastFour, " | Exp. ", formattedMonth, "/", formattedYear] }), card.isDefault && (_jsx("span", { className: styles.defaultBadge, children: " (Default)" }))] }, card.id));
                                                    })) : (_jsx("div", { className: styles.noPaymentMethod, children: "No payment methods on file" })) })] }), _jsxs("div", { className: styles.accountInfoSection, children: [_jsxs("div", { className: styles.accountField, children: [_jsx("label", { children: "Created By" }), _jsx("div", { className: styles.fieldValue, children: createdBy ? (_jsxs(_Fragment, { children: [_jsx("div", { children: createdBy.name || 'N/A' }), _jsx("div", { className: styles.subText, children: createdBy.email || '' })] })) : 'N/A' })] }), _jsxs("div", { className: styles.accountField, children: [_jsx("label", { children: "Created Date" }), _jsx("div", { className: styles.fieldValue, children: createdAt ? formatDateForTable(createdAt) : 'N/A' })] }), _jsxs("div", { className: styles.accountField, children: [_jsx("label", { children: "Modified By" }), _jsx("div", { className: styles.fieldValue, children: modifiedBy ? (_jsxs(_Fragment, { children: [_jsx("div", { children: modifiedBy.name || 'N/A' }), _jsx("div", { className: styles.subText, children: modifiedBy.email || '' })] })) : 'N/A' })] }), _jsxs("div", { className: styles.accountField, children: [_jsx("label", { children: "Modified Date" }), _jsx("div", { className: styles.fieldValue, children: modifiedAt ? formatDateForTable(modifiedAt) : 'N/A' })] }), _jsxs("div", { className: styles.accountField, children: [_jsx("label", { children: "Corporation" }), _jsx("div", { className: styles.fieldValue, children: clinic.customCorporationName || clinic.corporateGroup?.name || 'N/A' })] }), _jsxs("div", { className: styles.accountField, children: [_jsx("label", { children: "Sales Rep" }), _jsx("div", { className: styles.fieldValue, children: clinic.salesRepDetail
                                                                ? `${clinic.salesRepDetail.salesRepId} - ${clinic.salesRepDetail.name}`
                                                                : 'Unassigned' }), _jsx(Button, { variant: "secondaryV2", size: "small", label: clinic.salesRepDetail ? 'Change Sales Rep' : 'Assign Sales Rep', onClick: openAssignSalesRep })] }), _jsxs("div", { className: styles.accountField, children: [_jsx("label", { children: "Buying Groups" }), _jsx("div", { className: styles.fieldValue, children: clinic.buyingGroups && clinic.buyingGroups.length > 0 ? clinic.buyingGroups.join(', ') : 'N/A' })] }), _jsxs("div", { className: styles.accountField, children: [_jsx("label", { children: "Clinic Ownership Changed" }), _jsx("div", { className: styles.fieldValue, children: clinic.ownership_changed_at ? (_jsxs(_Fragment, { children: [_jsx("div", { children: formatDateForTable(clinic.ownership_changed_at) }), clinic.previous_owner && (_jsxs("div", { className: styles.subText, children: ["from ", clinic.previous_owner] }))] })) : 'N/A' })] }), _jsx("div", { className: styles.accountSectionBorder })] })] }) })] })] }), slideOutOpen && (_jsxs(_Fragment, { children: [_jsx("div", { className: styles.slideOutOverlay, onClick: () => {
                            setSlideOutOpen(false);
                            setSlideOutType(null);
                            setEditingContact(null);
                        } }), _jsxs("div", { className: styles.slideOutPanel, children: [_jsx("div", { className: styles.slideOutHeader, children: _jsxs("h2", { children: [slideOutType === 'editInfo' && 'Edit Clinic', slideOutType === 'changeStatus' && 'Change Status', slideOutType === 'salesRep' && 'Assign Sales Rep', slideOutType === 'contact' && (editingContact ? 'Edit Contact' : 'Add Contact')] }) }), _jsxs("div", { className: styles.slideOutContent, children: [slideOutType === 'editInfo' && clinic && clinicId && (_jsx(ClinicDetailsForm, { clinicId: clinicId, formData: editFormData, onSetFormData: setEditFormData, onSetClinic: setClinic })), slideOutType === 'salesRep' && clinicId && (_jsx(AssignSalesRepPanel, { clinicId: clinicId, clinicName: clinic?.name, currentSalesRep: clinic?.salesRepDetail
                                            ? {
                                                id: clinic.salesRepDetail.id,
                                                sales_rep_id: clinic.salesRepDetail.salesRepId,
                                                name: clinic.salesRepDetail.name,
                                            }
                                            : null, onSaved: (salesRep) => {
                                            setClinic((prev) => prev ? {
                                                ...prev,
                                                salesRep: salesRep?.id ?? null,
                                                salesRepDetail: salesRep
                                                    ? { id: salesRep.id, salesRepId: salesRep.sales_rep_id, name: salesRep.name }
                                                    : null,
                                            } : prev);
                                            setSlideOutOpen(false);
                                            setSlideOutType(null);
                                            loadSalesRepHistory(clinicId);
                                        } })), slideOutType === 'changeStatus' && (_jsxs("div", { children: [_jsxs("p", { style: { marginBottom: '16px', color: '#555' }, children: ["Select a new status for ", _jsx("strong", { children: clinic?.name }), "."] }), _jsx("div", { style: { display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '24px' }, children: [
                                                    { value: 'pending', label: 'Pending' },
                                                    { value: 'approved', label: 'Approved' },
                                                    { value: 'rejected', label: 'Rejected' },
                                                    { value: 'on_hold', label: 'On Hold' },
                                                ].map(({ value, label }) => (_jsxs("label", { style: { display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', fontSize: '14px' }, children: [_jsx("input", { type: "radio", name: "approvalStatus", value: value, checked: statusChangeValue === value, onChange: () => setStatusChangeValue(value) }), label] }, value))) }), statusChangeValue === 'rejected' && (_jsxs("div", { style: { marginBottom: '24px' }, children: [_jsx("label", { style: { display: 'block', marginBottom: '8px', fontSize: '14px' }, children: "Rejection Reason" }), _jsx("textarea", { value: rejectionReason, onChange: (e) => setRejectionReason(e.target.value), rows: 4, style: {
                                                            width: '100%',
                                                            fontFamily: 'inherit',
                                                            fontSize: '14px',
                                                            padding: '8px',
                                                            border: '1px solid #999',
                                                            borderRadius: '4px',
                                                            boxSizing: 'border-box',
                                                        } })] })), _jsx(Button, { label: statusChangeSaving ? 'Saving...' : 'Save Status', variant: "primary", disabled: statusChangeSaving ||
                                                    !statusChangeValue ||
                                                    (statusChangeValue === 'rejected' && !rejectionReason.trim()), onClick: async () => {
                                                    if (!clinicId || !statusChangeValue)
                                                        return;
                                                    setStatusChangeSaving(true);
                                                    try {
                                                        const payload = {
                                                            approval_status: statusChangeValue,
                                                        };
                                                        if (statusChangeValue === 'rejected') {
                                                            payload.rejection_reason = rejectionReason.trim();
                                                        }
                                                        await updateClinic(clinicId, payload);
                                                        setClinic((prev) => prev ? { ...prev, approval_status: statusChangeValue, approvalStatus: statusChangeValue, rejection_reason: payload.rejection_reason ?? prev.rejection_reason } : prev);
                                                        setSlideOutOpen(false);
                                                        setSlideOutType(null);
                                                    }
                                                    catch (err) {
                                                        alert('Failed to update status');
                                                    }
                                                    finally {
                                                        setStatusChangeSaving(false);
                                                    }
                                                } })] })), slideOutType === 'contact' && (_jsxs("div", { children: [_jsx("div", { style: { marginBottom: '16px' }, children: _jsx(Input, { label: "First Name", value: contactFormData.first_name, onChange: (value) => setContactFormData((prev) => ({ ...prev, first_name: value })), width: "100%" }) }), _jsx("div", { style: { marginBottom: '16px' }, children: _jsx(Input, { label: "Last Name", value: contactFormData.last_name, onChange: (value) => setContactFormData((prev) => ({ ...prev, last_name: value })), width: "100%" }) }), _jsx("div", { style: { marginBottom: '16px' }, children: _jsx(Input, { label: "Phone", value: contactFormData.phone_number, onChange: (value) => setContactFormData((prev) => ({ ...prev, phone_number: value })), width: "100%" }) }), _jsx("div", { style: { marginBottom: '16px' }, children: _jsx(Input, { label: "Email", type: "email", value: contactFormData.email, onChange: (value) => setContactFormData((prev) => ({ ...prev, email: value })), width: "100%" }) }), _jsxs("div", { style: { marginBottom: '24px' }, children: [_jsx("label", { htmlFor: "contact-role", style: { display: 'block', marginBottom: '8px', fontSize: '14px' }, children: "Clinic Role" }), _jsxs("select", { id: "contact-role", value: contactFormData.role, onChange: (e) => setContactFormData((prev) => ({ ...prev, role: e.target.value })), style: {
                                                            width: '100%',
                                                            height: '40px',
                                                            fontFamily: 'inherit',
                                                            fontSize: '14px',
                                                            padding: '0 8px',
                                                            border: '1px solid #999',
                                                            borderRadius: '4px',
                                                            boxSizing: 'border-box',
                                                        }, children: [_jsx("option", { value: "", children: "Select a role" }), CLINIC_CONTACT_ROLE_OPTIONS.map((option) => (_jsx("option", { value: option.value, children: option.label }, option.value)))] })] }), _jsxs("div", { style: { marginBottom: '16px' }, children: [_jsxs("label", { htmlFor: "contact-is-primary", style: { display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', cursor: 'pointer' }, children: [_jsx("input", { id: "contact-is-primary", type: "checkbox", checked: contactFormData.is_primary, onChange: (e) => setContactFormData((prev) => ({ ...prev, is_primary: e.target.checked })) }), "Primary contact"] }), contactFormData.is_primary && currentPrimaryContact && (_jsxs("div", { className: styles.primaryHint, children: ["This will replace ", contactDisplayName(currentPrimaryContact), " as the primary contact."] }))] })] }))] }), slideOutType === 'contact' && (_jsxs("div", { className: styles.slideOutFooter, children: [editingContact ? (_jsx(Button, { variant: "secondary", label: contactDeleting ? 'Removing...' : 'Delete', disabled: contactDeleting || contactSaving, onClick: handleDeleteContact })) : (_jsx("div", {})), _jsxs("div", { className: styles.slideOutFooterActions, children: [_jsx(Button, { variant: "secondary", label: "Cancel", disabled: contactSaving || contactDeleting, onClick: closeContactPanel }), _jsx(Button, { label: contactSaving ? 'Saving...' : editingContact ? 'Save Changes' : 'Add Contact', variant: "primary", disabled: contactSaving || contactDeleting, onClick: handleSaveContact })] })] }))] })] })), _jsx(CreateOrderModal, { isOpen: createOrderModalOpen, onClose: () => setCreateOrderModalOpen(false), onCreateOrder: handleCreateOrderSubmit, clinic: clinic })] }));
};
export default ClinicDetails;
